<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" 
         :style="{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }"
         @click.self="close">
      <div class="bg-white dark:bg-slate-850 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-150">
      
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center gap-3">
          <div class="p-2 bg-indigo-50 dark:bg-indigo-950/60 rounded-xl text-indigo-600 dark:text-indigo-400">
            <RadioIcon class="w-6 h-6" />
          </div>
          <div>
            <h3 class="text-base font-bold text-gray-900 dark:text-white">
              {{ $t('stageSync.title') }}
            </h3>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ $t('stageSync.subtitle') }}
            </p>
          </div>
        </div>
        <button @click="close" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700">
          <XMarkIcon class="w-5 h-5" />
        </button>
      </div>

      <!-- Mode Selector Tabs -->
      <div 
        :class="isNativeSupported ? 'grid-cols-3' : 'grid-cols-2'"
        class="grid p-3 gap-2 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-gray-800">
        <button 
          @click="selectMode('cloud')"
          :class="currentMode === 'cloud' ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium'"
          class="flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl text-xs transition-all duration-150">
          <CloudIcon class="w-4 h-4 flex-shrink-0" />
          <span class="truncate">{{ $t('stageSync.cloudMode') }}</span>
        </button>
        <button 
          @click="selectMode('local')"
          :class="currentMode === 'local' ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium'"
          class="flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl text-xs transition-all duration-150">
          <WifiIcon class="w-4 h-4 flex-shrink-0" />
          <span class="truncate">{{ $t('stageSync.localMode') }}</span>
          <span class="text-[9px] font-black uppercase px-1.5 py-0.2 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30">PRO</span>
        </button>
        <button 
          v-if="isNativeSupported"
          @click="selectMode('direct')"
          :class="currentMode === 'direct' ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm font-semibold' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium'"
          class="flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl text-xs transition-all duration-150">
          <RadioIcon class="w-4 h-4 flex-shrink-0" />
          <span class="truncate">{{ $t('stageSync.directMode') }}</span>
          <span class="text-[9px] font-black uppercase px-1.5 py-0.2 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30">PRO</span>
        </button>
      </div>

      <!-- Modal Body -->
      <div class="p-6 overflow-y-auto flex-1 space-y-5">
        
        <!-- Cloud Mode Explanation -->
        <div v-if="currentMode === 'cloud'" class="space-y-4 text-center py-2">
          <div class="w-14 h-14 mx-auto rounded-full bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
            <CloudIcon class="w-8 h-8" />
          </div>
          <div>
            <h4 class="text-sm font-semibold text-gray-900 dark:text-white">{{ $t('stageSync.cloudActiveTitle') }}</h4>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
              {{ $t('stageSync.cloudActiveDesc') }}
            </p>
          </div>
          <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
            <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            {{ $t('stageSync.cloudReadyBadge') }}
          </div>

          <!-- PRO Banner for Free Users -->
          <div v-if="isFree" class="mt-4 p-4 bg-gradient-to-br from-indigo-500/5 via-amber-500/10 to-indigo-500/5 dark:from-slate-800 dark:to-slate-850 rounded-2xl border border-amber-200/50 dark:border-amber-700/40 text-left">
            <div class="flex items-start gap-3">
              <div class="p-2 rounded-xl bg-amber-500/20 text-amber-600 dark:text-amber-400 shrink-0">
                <SparklesIcon class="w-5 h-5" />
              </div>
              <div class="flex-1 space-y-1">
                <div class="flex items-center gap-2">
                  <h5 class="text-xs font-bold text-gray-900 dark:text-white">
                    {{ $t('stageSync.proBannerTitle') }}
                  </h5>
                  <span class="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-md bg-amber-500 text-white shadow-xs">PRO</span>
                </div>
                <p class="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">
                  {{ $t('stageSync.proBannerDesc') }}
                </p>
                <button 
                  type="button"
                  @click="triggerSubscriptionModal" 
                  class="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-bold rounded-lg shadow-xs transition-all cursor-pointer">
                  <SparklesIcon class="w-3.5 h-3.5" />
                  <span>{{ $t('stageSync.proUpgradeAction') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Local WebRTC Mode with 4-Digit PIN -->
        <div v-else class="space-y-4">
          
          <!-- Role selector: Host vs Join -->
          <div class="flex items-center justify-center gap-2 p-1 bg-gray-100 dark:bg-slate-900 rounded-xl text-xs font-medium">
            <button 
              @click="localRole = 'host'" 
              :class="localRole === 'host' ? 'bg-white dark:bg-slate-800 shadow-sm text-indigo-600 dark:text-indigo-400 font-bold' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'"
              class="flex-1 py-1.5 rounded-lg transition-all flex items-center justify-center gap-1.5">
              <span>{{ $t('stageSync.roleHost') }}</span>
              <span v-if="isFree" class="text-[9px] font-black uppercase px-1.5 py-0.2 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30">PRO</span>
            </button>
            <button 
              @click="localRole = 'guest'" 
              :class="localRole === 'guest' ? 'bg-white dark:bg-slate-800 shadow-sm text-indigo-600 dark:text-indigo-400 font-bold' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'"
              class="flex-1 py-1.5 rounded-lg transition-all">
              {{ $t('stageSync.roleGuest') }}
            </button>
          </div>

          <!-- LÍDER / HOST VIEW -->
          <div v-if="localRole === 'host'" class="space-y-4 text-center">
            <p class="text-xs text-gray-500 dark:text-gray-400" v-html="currentMode === 'direct' ? $t('stageSync.hostHelpDirect') : $t('stageSync.hostHelp')"></p>

            <div v-if="!activePin" class="py-2">
              <button 
                @click="startHostSession" 
                :disabled="isGeneratingPin"
                class="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 mx-auto disabled:opacity-50">
                <ArrowPathIcon v-if="isGeneratingPin" class="w-4 h-4 animate-spin" />
                <SparklesIcon v-else class="w-4 h-4" />
                <span>{{ isGeneratingPin ? $t('stageSync.generatingPin') : (isFree ? $t('stageSync.proUpgradeAction') : $t('stageSync.startSessionBtn')) }}</span>
                <span v-if="isFree && !isGeneratingPin" class="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-md bg-amber-500 text-white shadow-xs">PRO</span>
              </button>
            </div>

            <!-- PIN Displayed -->
            <div v-else class="space-y-3">
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {{ $t('stageSync.pinLabel') }}
              </span>
              
              <div class="flex items-center justify-center gap-1.5 sm:gap-2 my-2 flex-wrap">
                <div v-for="(digit, idx) in activePin.split('')" :key="idx" 
                  class="w-10 h-12 sm:w-12 sm:h-14 bg-indigo-50 dark:bg-indigo-950/60 border-2 border-indigo-500/40 rounded-xl flex items-center justify-center text-xl sm:text-2xl font-black text-indigo-600 dark:text-indigo-400 shadow-sm">
                  {{ digit }}
                </div>
              </div>

              <p class="text-xs text-gray-600 dark:text-gray-300">
                {{ $t('stageSync.hostShareHelp') }}
              </p>

              <!-- Connected peers status -->
              <div class="p-3 bg-gray-50 dark:bg-slate-900 rounded-xl flex items-center justify-between text-xs border border-gray-200 dark:border-gray-800">
                <span class="text-gray-600 dark:text-gray-400 font-medium">{{ $t('stageSync.connectedMusicians') }}</span>
                <span class="font-bold text-sm text-indigo-600 dark:text-indigo-400">{{ connectedCount }}</span>
              </div>

              <button
                @click="disconnectStage"
                class="w-full py-2.5 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 text-xs font-semibold rounded-xl transition-all flex items-center justify-center gap-2">
                <XMarkIcon class="w-4 h-4" />
                <span>{{ $t('stageSync.disconnectBtn') }}</span>
              </button>
            </div>
          </div>

          <!-- MÚSICO / GUEST VIEW -->
          <div v-else class="space-y-4 text-center">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ $t('stageSync.guestHelp') }}
            </p>

            <div class="flex items-center justify-center gap-2 my-2">
              <input 
                v-model="inputPin"
                type="text" 
                :maxlength="currentMode === 'direct' ? 6 : 4" 
                placeholder="PIN"
                class="w-56 h-14 text-center tracking-[0.25em] text-2xl sm:text-3xl font-black font-mono bg-gray-50 dark:bg-slate-900 border-2 border-indigo-400 focus:border-indigo-600 rounded-xl focus:ring-0 focus:outline-none dark:text-white"
              />
            </div>

            <button 
              @click="joinSessionByPin"
              :disabled="inputPin.trim().length < 4 || isJoining"
              class="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <ArrowPathIcon v-if="isJoining" class="w-4 h-4 animate-spin" />
              <span>{{ isJoining ? $t('stageSync.connecting') : $t('stageSync.connectBtn') }}</span>
            </button>

            <div v-if="connectedCount > 0" class="p-3 bg-emerald-50 dark:bg-emerald-950/50 rounded-xl flex items-center justify-center gap-2 text-xs text-emerald-700 dark:text-emerald-400 font-semibold border border-emerald-200 dark:border-emerald-800">
              <CheckCircleIcon class="w-4 h-4 text-emerald-500" />
              <span>{{ $t('stageSync.connectedSuccess') }}</span>
            </div>

            <button
              v-if="connectedCount > 0"
              @click="disconnectStage"
              class="w-full py-2.5 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 text-xs font-semibold rounded-xl transition-all flex items-center justify-center gap-2">
              <XMarkIcon class="w-4 h-4" />
              <span>{{ $t('stageSync.disconnectBtn') }}</span>
            </button>
          </div>

        </div>

      </div>

      <!-- Footer -->
      <div class="px-6 py-3 bg-gray-50 dark:bg-slate-900/50 border-t border-gray-100 dark:border-gray-800 flex justify-end">
        <button @click="close" class="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-xl transition-all">
          {{ $t('stageSync.close') }}
        </button>
      </div>

    </div>
  </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineExpose } from 'vue';
import { useI18n } from 'vue-i18n';
import { 
  XMarkIcon, RadioIcon, CloudIcon, WifiIcon, 
  SparklesIcon, CheckCircleIcon, ArrowPathIcon 
} from '@heroicons/vue/24/outline';
import transportManager from '@/service/realtime/transport.manager';
import nativeP2PService from '@/service/realtime/native-p2p.provider';
import toastStore from '@/store/toast.store';
import userStore from '@/store/user.store';
import { appEvents, openSubscriptionModal } from '@/store/app.events';

const { t } = useI18n();

const isOpen = ref(false);
const currentMode = ref(transportManager.getMode());
const isNativeSupported = ref(false);
const localRole = ref('host'); // 'host' | 'guest'

const isFree = computed(() => {
  try {
    return !!userStore?.getters?.['isFree'];
  } catch (e) {
    return false;
  }
});

const isGeneratingPin = ref(false);
const activePin = ref('');

const isJoining = ref(false);
const inputPin = ref('');

const connectedCount = ref(0);
let countInterval = null;
let pollAnswersInterval = null;

const checkNativeSupport = async () => {
  try {
    isNativeSupported.value = await nativeP2PService.isSupported();
  } catch (e) {
    isNativeSupported.value = false;
  }
};

const triggerSubscriptionModal = () => {
  close();
  openSubscriptionModal();
};

const selectMode = (mode) => {
  currentMode.value = mode;
  transportManager.setMode(mode);
  activePin.value = '';
  inputPin.value = '';
  stopHostPolling();
  updateCount();

  // Usuários free entram como 'guest' (Músico) para sincronizar com o PIN do Líder
  if (isFree.value && mode !== 'cloud') {
    localRole.value = 'guest';
  }

  let msgKey = 'stageSync.toastCloudActivated';
  if (mode === 'local') msgKey = 'stageSync.toastLocalActivated';
  if (mode === 'direct') msgKey = 'stageSync.toastDirectActivated';

  toastStore.dispatch('addMessage', {
    message: t(msgKey),
    icon: 'info',
    ttl: 3000
  });
};

const startHostSession = async () => {
  if (isFree.value && currentMode.value !== 'cloud') {
    toastStore.dispatch('addMessage', {
      message: t('stageSync.proUpgradeRequired'),
      icon: 'warning',
      ttl: 5000
    });
    triggerSubscriptionModal();
    return;
  }

  isGeneratingPin.value = true;
  try {
    if (currentMode.value === 'direct') {
      // Gera PIN aleatório de 6 dígitos para P2P Nativo
      const pin = Math.floor(100000 + Math.random() * 900000).toString();
      await nativeP2PService.startHost(pin);
      activePin.value = pin;
      toastStore.dispatch('addMessage', { message: t('stageSync.toastSessionStarted', { pin }), icon: 'success' });
    } else {
      const webrtc = transportManager.getWebRTC();
      const pin = await webrtc.startHostPinSession();
      activePin.value = pin;
      toastStore.dispatch('addMessage', { message: t('stageSync.toastSessionStarted', { pin }), icon: 'success' });
      
      // Inicia polling das respostas dos músicos via sinalização LAN
      if (pollAnswersInterval) clearInterval(pollAnswersInterval);
      pollAnswersInterval = setInterval(async () => {
        await webrtc.pollHostAnswers();
        updateCount();
      }, 1500);
    }
  } catch (err) {
    console.error('Erro ao criar sessão host:', err);
    toastStore.dispatch('addMessage', { message: err?.message || t('stageSync.toastGeneratePinError'), icon: 'error' });
  } finally {
    isGeneratingPin.value = false;
  }
};

const joinSessionByPin = async () => {
  const minLen = currentMode.value === 'direct' ? 6 : 4;
  if (inputPin.value.trim().length < minLen) return;
  isJoining.value = true;
  try {
    if (currentMode.value === 'direct') {
      await nativeP2PService.joinByPin(inputPin.value.trim());
      toastStore.dispatch('addMessage', { message: t('stageSync.toastConnectedLeader'), icon: 'success' });
    } else {
      const webrtc = transportManager.getWebRTC();
      await webrtc.joinByPin(inputPin.value);
      toastStore.dispatch('addMessage', { message: t('stageSync.toastConnectedLeader'), icon: 'success' });
    }
    updateCount();
  } catch (err) {
    console.error('Erro ao conectar com PIN:', err);
    toastStore.dispatch('addMessage', { message: err.message || t('stageSync.toastConnectError'), icon: 'error' });
  } finally {
    isJoining.value = false;
  }
};

const updateCount = () => {
  if (currentMode.value === 'direct') {
    connectedCount.value = nativeP2PService.getConnectedCount();
  } else {
    connectedCount.value = transportManager.getWebRTC().getConnectedCount();
  }
};

const stopHostPolling = () => {
  if (pollAnswersInterval) {
    clearInterval(pollAnswersInterval);
    pollAnswersInterval = null;
  }
};

// Desconecta do modo ativo (WebRTC ou Native P2P) e volta para a nuvem (Ably).
const disconnectStage = async () => {
  try {
    stopHostPolling();
    if (currentMode.value === 'direct') {
      await nativeP2PService.disconnect();
    } else {
      transportManager.getWebRTC().disconnect();
    }
  } catch (e) {
    console.warn('[StageSyncModal] disconnectStage:', e);
  }
  activePin.value = '';
  inputPin.value = '';
  updateCount();
  if (transportManager.getMode() !== 'cloud') {
    currentMode.value = 'cloud';
    transportManager.setMode('cloud');
  }
  toastStore.dispatch('addMessage', { message: t('stageSync.toastDisconnected'), icon: 'info', ttl: 3000 });
};

const open = () => {
  currentMode.value = transportManager.getMode();
  if (isFree.value && currentMode.value !== 'cloud') {
    localRole.value = 'guest';
  }
  checkNativeSupport();
  updateCount();
  isOpen.value = true;
};

const close = () => {
  isOpen.value = false;
};

onMounted(() => {
  appEvents.on('open-stage-sync-modal', open);
  countInterval = setInterval(updateCount, 1500);
});

onUnmounted(() => {
  appEvents.off('open-stage-sync-modal', open);
  if (countInterval) clearInterval(countInterval);
  if (pollAnswersInterval) clearInterval(pollAnswersInterval);
});

defineExpose({ open, close });
</script>
